<script>
/**
 * @component PharmacyList
 * @description ABM de farmacias: stats, búsqueda, filtros, tabla paginada,
 *              importación masiva y dialog de alta/edición con secciones agrupadas.
 * @example <PharmacyList />
 */
import pharmacyService from "@/services/pharmacy";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import { mapGetters } from "vuex";

const DEBOUNCE_MS = 500;

const STATUS_OPTIONS = [
  { value: "active", label: "Activo" },
  { value: "inactive", label: "Inactivo" },
];

export default {
  name: "PharmacyList",

  components: { Spinner },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      filterStatus: "",
      pharmacies: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      stats: { total: 0, delivery: 0, online: 0 },
      statusOptions: STATUS_OPTIONS,
      // Dialog
      isDialogVisible: false,
      dialogMode: "create",
      editingId: null,
      form: {
        businessName: "",
        cuit: "",
        commercialName: "",
        email: "",
        address: "",
        city: "",
        province: "",
        phone: "",
        latitude: "",
        longitude: "",
        delivery: false,
        online: false,
        onlineUrl: "",
        schedule: "",
        website: "",
        status: "active",
      },
      formErrors: {},
      // Import
      isImportVisible: false,
      importFile: null,
      isImporting: false,
      importResult: null,
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),
  },

  created() {
    this.fetchPharmacies();
  },

  methods: {
    /**
     * @description Obtiene las farmacias del backend con los filtros aplicados
     */
    async fetchPharmacies() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterStatus) params.status = this.filterStatus;

        const result = await pharmacyService.query(params);
        this.pharmacies = result.docs || [];
        this.total = result.total || 0;

        this.stats = {
          total: result.total || 0,
          delivery: result.deliveryCount || 0,
          online: result.onlineCount || 0,
        };
      } catch (error) {
        this.$message.error("Error al cargar farmacias");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    debounceSearch: debounce(function () {
      this.currentPage = 1;
      this.fetchPharmacies();
    }, DEBOUNCE_MS),

    onFilterChange() {
      this.currentPage = 1;
      this.fetchPharmacies();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchPharmacies();
    },

    /**
     * @description Devuelve el label del estado
     * @param {string} status - Valor
     * @returns {string}
     */
    getStatusLabel(status) {
      const opt = STATUS_OPTIONS.find((o) => o.value === status);
      return opt ? opt.label : status || "-";
    },

    /**
     * @description Devuelve la clase badge del estado
     * @param {string} status - Valor
     * @returns {string}
     */
    getStatusBadge(status) {
      return status === "active" ? "badge--success" : "badge--neutral";
    },

    /**
     * @description Verifica si la farmacia tiene coordenadas de geolocalización
     * @param {Object} pharmacy - Farmacia
     * @returns {boolean}
     */
    hasLocation(pharmacy) {
      const coords = pharmacy.location?.coordinates;
      return Array.isArray(coords) && coords.length === 2 && coords[0] !== 0;
    },

    // --- Dialog ---

    /**
     * @description Abre el dialog para crear o editar una farmacia
     * @param {string} mode - 'create' o 'edit'
     * @param {Object} [pharmacy] - Farmacia a editar
     */
    openDialog(mode = "create", pharmacy = null) {
      this.dialogMode = mode;
      this.isDialogVisible = true;
      this.formErrors = {};

      if (mode === "edit" && pharmacy) {
        this.editingId = pharmacy._id;
        this.form = {
          businessName: pharmacy.businessName || "",
          cuit: pharmacy.taxId || pharmacy.cuit || "",
          commercialName: pharmacy.commercialName || "",
          email: pharmacy.email || "",
          address: pharmacy.address || "",
          city: pharmacy.city || "",
          province: pharmacy.province || "",
          phone: pharmacy.phone || "",
          latitude: pharmacy.location?.coordinates?.[1] ?? "",
          longitude: pharmacy.location?.coordinates?.[0] ?? "",
          delivery: !!pharmacy.hasDelivery,
          online: !!pharmacy.sellsOnline,
          onlineUrl: pharmacy.onlineStoreUrl || "",
          schedule: pharmacy.schedule || "",
          website: pharmacy.website || "",
          status: pharmacy.status || "active",
        };
      } else {
        this.resetForm();
      }
    },

    closeDialog() {
      this.isDialogVisible = false;
      this.resetForm();
    },

    resetForm() {
      this.editingId = null;
      this.form = {
        businessName: "",
        cuit: "",
        commercialName: "",
        email: "",
        address: "",
        city: "",
        province: "",
        phone: "",
        latitude: "",
        longitude: "",
        delivery: false,
        online: false,
        onlineUrl: "",
        schedule: "",
        website: "",
        status: "active",
      };
      this.formErrors = {};
    },

    /**
     * @description Valida el formulario antes de guardar
     * @returns {boolean} true si es válido
     */
    validateForm() {
      this.formErrors = {};

      if (!this.form.businessName.trim()) {
        this.formErrors.businessName = "Razón social requerida";
      }
      if (!this.form.commercialName.trim()) {
        this.formErrors.commercialName = "Nombre comercial requerido";
      }

      return Object.keys(this.formErrors).length === 0;
    },

    /**
     * @description Guarda la farmacia (crear o actualizar)
     */
    async savePharmacy() {
      if (!this.validateForm()) return;

      try {
        const payload = { ...this.form };
        payload.businessName = payload.businessName.trim();
        payload.commercialName = payload.commercialName.trim();
        // El backend usa taxId, no cuit
        payload.taxId = (payload.cuit || "").trim();
        delete payload.cuit;
        if (payload.email) payload.email = payload.email.trim();
        if (payload.address) payload.address = payload.address.trim();
        if (payload.city) payload.city = payload.city.trim();
        if (payload.province) payload.province = payload.province.trim();
        if (payload.phone) payload.phone = payload.phone.trim();
        if (payload.schedule) payload.schedule = payload.schedule.trim();
        if (payload.website) payload.website = payload.website.trim();
        // El backend usa hasDelivery, sellsOnline, onlineStoreUrl
        payload.hasDelivery = payload.delivery;
        payload.sellsOnline = payload.online;
        payload.onlineStoreUrl = payload.sellsOnline
          ? (payload.onlineUrl || "").trim()
          : "";
        delete payload.delivery;
        delete payload.online;
        delete payload.onlineUrl;

        // Construir GeoJSON si hay coordenadas
        const lat = parseFloat(payload.latitude);
        const lng = parseFloat(payload.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          payload.location = {
            type: "Point",
            coordinates: [lng, lat],
          };
        } else {
          payload.location = null;
        }
        delete payload.latitude;
        delete payload.longitude;

        if (this.dialogMode === "create") {
          await pharmacyService.create(payload);
          this.$message.success("Farmacia creada");
        } else {
          await pharmacyService.update(this.editingId, payload);
          this.$message.success("Farmacia actualizada");
        }
        this.closeDialog();
        this.fetchPharmacies();
      } catch (error) {
        this.$message.error(
          error.response?.data?.error || "Error al guardar farmacia"
        );
      }
    },

    /**
     * @description Elimina una farmacia previa confirmación
     * @param {Object} pharmacy - Farmacia a eliminar
     */
    async deletePharmacy(pharmacy) {
      try {
        await this.$confirm(
          `¿Eliminar la farmacia "${
            pharmacy.commercialName || pharmacy.businessName
          }"?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await pharmacyService.remove(pharmacy._id);
        this.$message.success("Farmacia eliminada");
        this.fetchPharmacies();
      } catch {
        // Cancelado
      }
    },

    // --- Import ---

    openImport() {
      this.isImportVisible = true;
      this.importFile = null;
      this.importResult = null;
    },

    closeImport() {
      this.isImportVisible = false;
      this.importFile = null;
      this.importResult = null;
    },

    /**
     * @description Captura el archivo seleccionado
     * @param {Event} event - Evento del input file
     */
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.importFile = file;
        this.importResult = null;
      }
    },

    /**
     * @description Ejecuta la importación del archivo Excel/CSV
     */
    async executeImport() {
      if (!this.importFile) {
        this.$message.warning("Seleccioná un archivo primero");
        return;
      }
      this.isImporting = true;
      this.importResult = null;
      try {
        const result = await pharmacyService.import(this.importFile);
        this.importResult = result;
        if (result.imported > 0) {
          this.$message.success(`Se importaron ${result.imported} farmacias`);
          this.fetchPharmacies();
        }
      } catch (error) {
        this.$message.error(error.response?.data?.error || "Error al importar");
      } finally {
        this.isImporting = false;
      }
    },
  },
};
</script>

<template lang="pug">
section
  //- Stats
  .pharmacy-stats
    .pharmacy-stats__card
      .pharmacy-stats__value {{ stats.total }}
      .pharmacy-stats__label Total farmacias
    .pharmacy-stats__card.pharmacy-stats__card--success
      .pharmacy-stats__value {{ stats.delivery }}
      .pharmacy-stats__label Con delivery
    .pharmacy-stats__card.pharmacy-stats__card--info
      .pharmacy-stats__value {{ stats.online }}
      .pharmacy-stats__label Venta online

  //- Filters
  header.headline
    .headline__actions
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        @input="debounceSearch"
        placeholder="Buscar por nombre o CUIT"
        :clearable="true"
        size="small"
      )
      el-select(
        v-model="filterStatus"
        placeholder="Estado"
        clearable
        @change="onFilterChange"
        size="small"
      )
        el-option(
          v-for="opt in statusOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        )
      .button.button--ghost(@click="openImport()" v-if="isAdmin")
        micon(name="cloud_upload").button__icon
        span.button__text Importar Excel
      .button.button--navy(@click="openDialog('create')" v-if="isAdmin")
        micon(name="add_circle_outline").button__icon
        span.button__text Nueva Farmacia

  //- Table
  .box.box--with-subnav
    .box__content--stretch
      table
        thead
          tr
            th Nombre comercial
            th CUIT
            th Dirección
            th Localidad
            th Ubicación
            th Delivery
            th Online
            th Estado
            th Acciones
        tbody
          spinner(v-if="isLoading")
          tr(
            v-else
            v-for="p in pharmacies"
            :key="p._id"
            @click="openDialog('edit', p)"
          )
            td {{ p.commercialName || p.businessName || '-' }}
            td {{ p.taxId || p.cuit || '-' }}
            td {{ p.address || '-' }}
            td {{ p.city || '-' }}
            td.td-center
              micon.icon-check(v-if="hasLocation(p)" name="check_circle")
              micon.icon-cross(v-else name="cancel")
            td.td-center
              micon.icon-check(v-if="p.hasDelivery" name="check_circle")
              micon.icon-cross(v-else name="cancel")
            td.td-center
              micon.icon-check(v-if="p.sellsOnline" name="check_circle")
              micon.icon-cross(v-else name="cancel")
            td
              span.badge(:class="getStatusBadge(p.status)") {{ getStatusLabel(p.status) }}
            td.actions
              .actions-container(@click.stop)
                button.button.button--action(@click="openDialog('edit', p)")
                  micon(name="edit")
                button.button.button--action(@click="deletePharmacy(p)" v-if="isAdmin")
                  micon(name="delete_forever")

    el-pagination(
      v-if="total > pageSize"
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page="currentPage"
      @current-change="onPageChange"
    )

  //- Create/Edit dialog
  el-dialog(
    :title="dialogMode === 'create' ? 'Nueva Farmacia' : 'Editar Farmacia'"
    :visible.sync="isDialogVisible"
    width="720px"
    :close-on-click-modal="false"
  )
    .pharmacy-form
      //- DATOS FISCALES
      .form-section
        .form-section__title Datos fiscales
        .form-row
          .form-field
            label.form-field__label
              span.required *
              |  Razón social
            el-input(v-model="form.businessName" size="small" maxlength="100" placeholder="Ej: Farmacia San Martín S.R.L.")
            span.has-error(v-if="formErrors.businessName") {{ formErrors.businessName }}
          .form-field
            label.form-field__label CUIT
            el-input(v-model="form.cuit" size="small" maxlength="13" placeholder="Ej: 30-12345678-9")
        .form-row
          .form-field
            label.form-field__label
              span.required *
              |  Nombre comercial
            el-input(v-model="form.commercialName" size="small" maxlength="100" placeholder="Ej: Farmacia San Martín")
            span.has-error(v-if="formErrors.commercialName") {{ formErrors.commercialName }}
          .form-field
            label.form-field__label Email
            el-input(v-model="form.email" size="small" maxlength="100" placeholder="Ej: contacto@farmacia.com")

      //- UBICACIÓN
      .form-section
        .form-section__title Ubicación
        .form-row
          .form-field
            label.form-field__label Dirección
            el-input(v-model="form.address" size="small" maxlength="200" placeholder="Ej: Av. Corrientes 1234")
          .form-field
            label.form-field__label Localidad
            el-input(v-model="form.city" size="small" maxlength="100" placeholder="Ej: CABA")
        .form-row
          .form-field
            label.form-field__label Provincia
            el-input(v-model="form.province" size="small" maxlength="100" placeholder="Ej: Buenos Aires")
          .form-field
            label.form-field__label Teléfono
            el-input(v-model="form.phone" size="small" maxlength="20" placeholder="Ej: 11-1234-5678")
        .form-row
          .form-field
            label.form-field__label Latitud
            el-input(v-model="form.latitude" size="small" placeholder="Ej: -34.5883")
          .form-field
            label.form-field__label Longitud
            el-input(v-model="form.longitude" size="small" placeholder="Ej: -58.4267")

      //- SERVICIOS
      .form-section
        .form-section__title Servicios
        .form-row
          .form-field
            label.form-field__label Hace delivery
            el-switch(v-model="form.delivery")
          .form-field
            label.form-field__label Vende online
            el-switch(v-model="form.online")
        .form-row.form-row--1col(v-if="form.online")
          .form-field
            label.form-field__label URL tienda online
            el-input(v-model="form.onlineUrl" size="small" maxlength="200" placeholder="Ej: https://tienda.farmacia.com")
        .form-row.form-row--1col
          .form-field
            label.form-field__label Horario de atención
            el-input(v-model="form.schedule" size="small" maxlength="100" placeholder="Ej: Lunes a Viernes 8:00 - 20:00")

      //- SITIO WEB
      .form-section
        .form-section__title Sitio web
        .form-row.form-row--1col
          .form-field
            label.form-field__label Sitio web
            el-input(v-model="form.website" size="small" maxlength="200" placeholder="Ej: https://www.farmacia.com")

    div(slot="footer")
      el-button(@click="closeDialog" size="small") Cancelar
      el-button(type="primary" @click="savePharmacy" size="small") Guardar

  //- Import dialog
  el-dialog(title="Importar Farmacias desde Excel" :visible.sync="isImportVisible" width="500px")
    .import-form
      el-form-item(label="Archivo Excel o CSV")
        input.file-input(type="file" accept=".xlsx,.xls,.csv" @change="onFileSelected")
      .import-result(v-if="importResult")
        el-alert(
          :title="`${importResult.imported} de ${importResult.total} importadas`"
          :type="importResult.imported === importResult.total ? 'success' : 'warning'"
          :closable="false"
          show-icon
        )
    div(slot="footer")
      el-button(@click="closeImport") Cerrar
      el-button(type="primary" @click="executeImport" :loading="isImporting" :disabled="!importFile") Importar
</template>

<style lang="scss" scoped>
.pharmacy-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  &__card {
    background: #fff;
    border: 1px solid $border-color;
    border-radius: 12px;
    padding: 16px 20px;
    text-align: center;

    &--success {
      border-left: 3px solid #22c55e;
    }
    &--info {
      border-left: 3px solid #3b82f6;
    }
  }

  &__value {
    font-size: 24px;
    font-weight: 800;
    color: $text-primary-new;
  }

  &__label {
    font-size: 12px;
    color: $text-secondary-new;
    margin-top: 2px;
  }
}

.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search__input {
  width: 280px;
}
.el-select {
  width: 160px;
}

.td-center {
  text-align: center;
}
.icon-check {
  fill: #22c55e;
  width: 18px;
  height: 18px;
}
.icon-cross {
  fill: #cbd5e1;
  width: 18px;
  height: 18px;
}

// ─── Pharmacy form (dialog) ───
.pharmacy-form {
  padding: 0 4px;
}

.form-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  background: white;

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    margin-bottom: 16px;
  }
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 12px;

  &--1col {
    grid-template-columns: 1fr;
  }
}

.form-field {
  min-width: 0;

  .el-select,
  .el-date-editor {
    width: 100%;
  }
}

.form-field__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7294;
  margin-bottom: 4px;

  .required {
    color: #ef4444;
    margin-right: 2px;
  }
}

.has-error {
  color: #ef4444;
  font-size: 12px;
}

.file-input {
  width: 100%;
  padding: 8px 0;
}
.import-form {
  padding: 0 5px;
}
.import-result {
  margin-top: 16px;
}

tbody td:hover {
  cursor: pointer;
}
</style>
